import { useEffect, useState } from 'react';

const REDUCE_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Step 61 (PR-A11Y.E) — programmatic mirror of GAP-65's CSS-only
 * `prefers-reduced-motion` contract.
 *
 * The CSS universal `*!important` rule in `index.css` zeros transition +
 * animation durations across most surfaces, but it loses the cascade contest
 * to `@layer utilities`-scoped Tailwind utilities (e.g. `duration-200`) and
 * to inline `style={{ animationDuration }}` values that React components set
 * imperatively. Both are common: `animate-in slide-in-from-*` chrome on
 * `DashboardLayout` and inline-styled fade/transition wrappers on
 * `CourseLayout`. Components that own those surfaces must opt into reduce by
 * branching their style/className on this hook so WCAG 2.3.3 (AAA) holds.
 *
 * Returns `false` during SSR / non-`window` environments and during the test
 * setup's matchMedia mock (`src/test/setup.ts` returns `matches: false`).
 */
export function useReducedMotion(): boolean {
  const [reduce, setReduce] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(REDUCE_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(REDUCE_QUERY);
    const handler = (event: MediaQueryListEvent) => setReduce(event.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return reduce;
}
